import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column, ExpandedColumn } from '@repo/ui/data-table'
import {
  EmailIcon,
  PlusIcon,
  ProviderIcon,
  UploadIcon,
  UserCanceledIcon,
  UserCheckIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import RoundedIcon from '@repo/ui/rounded-icon'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { connectAsOl } from '../../api/ols/connect-as-ol'
import { sendEmailToOls } from '../../api/ols/send-email-to-ols'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import { getUserDefaultRedirectionPath } from '../../components/pages/login/utils'
import CcParisContactsCheckbox from '../../components/pages/ols/cc-paris-contacts-checkbox'
import { OlsFilter } from '../../components/pages/ols/ols-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { OLTypeEnum, olTypeEnumMapping } from '../../lib/types/enums/ol/ol-type.enum'
import { Ol, OlFilters } from '../../lib/types/models/ol/ol'
import { OlContact } from '../../lib/types/models/ol/ol-contact'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformOlFiltersToUrl,
  transformUrlToOlFilters
} from '../../lib/utils/filters-mapping/ol'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../utils/functions/format-phone-number'
import { getOlName } from './center/utils/functions'
import { constructOlPath } from './views/utils'

export const olInitialFilter: OlFilters = {
  isActive: true
}

export function OlListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<Ol>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [connectButtonId, setConnectButtonId] = useState<number>()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'send-email',
    (Ol | OlContact)[]
  >(['filters', 'send-email'])

  const { mutate: connectAsOlMutation, isPending: connectAsOlPending } = useMutation({
    mutationFn: connectAsOl,
    onSuccess: (data) => {
      // A full page load: nothing of the previous session keeps running
      window.location.assign(getUserDefaultRedirectionPath(data))
    },
    onError: showAxiosError
  })

  const { mutateAsync: sendEmailToOlsMutation } = useMutation({
    mutationFn: sendEmailToOls,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const cancelSendEmail = () => toggleModal('send-email')
  const emails = selectedData
    ? selectedData.map((row) => {
        if ('mainContactEmail' in row) return row.mainContactEmail
        return row.email
      })
    : []

  const parisContactEmails = Array.from(
    new Set(
      (selectedData ?? []).flatMap((row) =>
        'contactInParis' in row && row.contactInParis ? [row.contactInParis.email] : []
      )
    )
  )

  const getEmailDefaultValues = (ol: (Ol | OlContact)[]) => {
    return {
      emails: ol.map((row) => {
        if ('mainContactEmail' in row) return row.mainContactEmail
        return row.email
      }),
      cc: [],
      subject: '',
      body: ''
    }
  }

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OlFilters>({
      initialFilters: olInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOlFiltersToUrl,
      transformUrlToFilter: transformUrlToOlFilters
    })

  const columns: Column<Ol>[] = [
    {
      accessorKey: 'mainContactEmail',
      header: '',
      cell: ({ row }) => (
        <RoundedIcon
          size={30}
          variant="primary"
          icon={EmailIcon}
          onClick={(e) => {
            e.stopPropagation()
            window.location.href = `mailto:${row.original.mainContactEmail}`
          }}
        />
      )
    },
    {
      accessorKey: 'isActive',
      header: '',
      cell: ({ row }) =>
        row.original.isActive ? (
          <RoundedIcon variant="success" icon={UserCheckIcon} />
        ) : (
          <RoundedIcon variant="danger" icon={UserCanceledIcon} />
        )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant={olTypeEnumMapping[row.original.type].variant}
          text={olTypeEnumMapping[row.original.type].label}
        />
      )
    },
    {
      accessorKey: 'mainContactPicture',
      header: 'Photo',
      cell: ({ row }) =>
        row.original.mainContactPicture ? (
          <img
            src={row.original.mainContactPicture?.path}
            alt="profile"
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'mainContactLastName',
      header: 'NOM',
      enableSorting: true,
      cell: ({ row }) => row.original.mainContactLastName.toLocaleUpperCase()
    },
    {
      accessorKey: 'mainContactFirstName',
      header: 'Prénom',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.mainContactFirstName
          ? StringFormatter.capitalize(row.original.mainContactFirstName)
          : '-'
    },
    {
      accessorKey: 'mainContactPhone',
      header: 'Téléphone',
      cell: ({ row }) => formatPhoneNumber(row.original.mainContactPhone)
    },
    {
      accessorKey: 'hostCountries',
      header: 'Lieu de séjour',
      cell: ({ row }) => row.original.hostCountries.map((country) => country.name).join(', ')
    },
    {
      accessorKey: 'contactInParis.firstName',
      header: 'Contact à paris',
      enableSorting: true,
      cell: ({ row }) => {
        const contactInParis = row.original.contactInParis
        return contactInParis
          ? toFullName(contactInParis?.firstName, contactInParis?.lastName)
          : ' - '
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Année début',
      enableSorting: true,
      cell: ({ row }) => DateFormatter.toYear(row.original.createdAt)
    },
    {
      accessorKey: 'deactivatedAt',
      header: 'Année fin',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.deactivatedAt ? DateFormatter.toYear(row.original.deactivatedAt) : ' - '
    },
    {
      accessorKey: 'id',
      header: 'Espace personnel',
      cell: ({ row }) => {
        if (row.original.type === OLTypeEnum.DIVERS) return
        return (
          <Button
            variant="primary"
            label="Se connecter à son espace"
            loading={connectAsOlPending && connectButtonId === row.original.id}
            onClick={(e) => {
              setConnectButtonId(row.original.id)
              e.stopPropagation()
              connectAsOlMutation(row.original.id)
            }}
          />
        )
      }
    }
  ]

  const olContactsColumns: ExpandedColumn<Ol, OlContact>[] = [
    {
      accessorKey: 'mainContactLastName',
      display: (row) => row?.lastName.toUpperCase()
    },
    {
      accessorKey: 'mainContactFirstName',
      display: (row) => StringFormatter.capitalize(row?.firstName)
    },
    {
      accessorKey: 'mainContactPhone',
      display: (row) => formatPhoneNumber(row?.phone)
    },
    {
      accessorKey: 'hostCountries',
      display: (row) => row?.position
    }
  ]

  return (
    <div>
      <UserNavBar title={'Liste des prestataires'} icon={ProviderIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Ol, OlFilters, OlContact>
        ref={tableRef}
        legends={[
          {
            icon: <RoundedIcon variant="success" icon={UserCheckIcon} />,
            label: 'Actif'
          },
          {
            icon: <RoundedIcon variant="danger" icon={UserCanceledIcon} />,
            label: 'Inactif'
          }
        ]}
        expandedOptions={{ key: 'olContacts', columns: olContactsColumns }}
        onLoadingChange={setIsTableLoading}
        title={'Tous les prestataires'}
        deleteMessage={{ name: 'prestataires', attributeName: 'mainContactFirstName' }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(ol, event) =>
          navigateWithEvent(constructOlPath(Paths.OL_DETAILS_UPDATE_PAGE, ol.id), event)
        }
        endpoint={API.ols.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(filter)}
        version="FULL"
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            label: 'Ajouter un prestataire',
            icon: PlusIcon,
            fn: () => navigate(Paths.OL_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Export CSV IBAN Cedricom',
            fn: (ols) => {
              const ids = ols.map((ol) => ol.id)
              if (ids.length === 0) toast.error('Veuillez sélectionner un prestataire')
              else
                downloadDocuments({
                  endpoint: API.ols.exportIbansExcel.url,
                  ids
                })
            }
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Envoyer un Email',
            variant: 'ghost',
            fn: (ols, olContacts) => {
              toggleModal('send-email', [...ols, ...(olContacts ?? [])])
            }
          }
        ]}
      />

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={cancelSendEmail}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={getEmailDefaultValues(selectedData)}
            labels={selectedData.map((ol) => {
              const isOl = 'mainContactEmail' in ol
              if (isOl) return `${getOlName(ol)} - ${ol.mainContactEmail}`
              return `${toFullName(ol.firstName, ol.lastName)} - ${ol.email}`
            })}
            buttonsConfig={modalButtonConfig({
              onCancel: cancelSendEmail,
              onConfirm: (values) => sendEmailToOlsMutation({ ...values, emails })
            })}
            customComponent={<CcParisContactsCheckbox parisContactEmails={parisContactEmails} />}
          />
        </Modal>
      )}

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OlsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
